import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Clock, MapPin, Package, Paperclip, ShieldCheck, Wrench, XCircle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { STATUS_LABELS, formatDateIT } from "@/lib/api";
import AmaLogo from "@/components/app/AmaLogo";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * QRRedirect handles the `/q/:token` URL that every printed QR code encodes.
 *
 * Routing logic:
 *  - authenticated staff (any role) → resolve the token via /api/qr/resolve/{token}
 *    and navigate to /pratiche/{case_id};
 *  - unauthenticated visitors → render a minimal public status page reading
 *    /api/public/track/{token}. The public page shows only the essentials
 *    (case_number, customer name, items, current status per item, last update)
 *    and a secure link to the existing quotation approval page if one exists.
 */
export default function QRRedirect() {
  const { token } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, error: null, data: null });

  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (user) {
        try {
          const jwt = localStorage.getItem("ama_token");
          const r = await axios.get(`${API}/qr/resolve/${token}`, {
            headers: { Authorization: `Bearer ${jwt}` },
          });
          if (!cancelled) navigate(`/pratiche/${r.data.case_id}`, { replace: true });
          return;
        } catch (e) {
          if (!cancelled) setState({ loading: false, error: e?.response?.data?.detail || "QR non valido", data: null });
          return;
        }
      }
      try {
        const r = await axios.get(`${API}/public/track/${token}`);
        if (!cancelled) setState({ loading: false, error: null, data: r.data });
      } catch (e) {
        if (!cancelled) setState({ loading: false, error: e?.response?.data?.detail || "Codice QR non valido o revocato", data: null });
      }
    }
    run();
    return () => { cancelled = true; };
  }, [token, user, navigate]);

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500 text-sm">Verifica in corso…</div>
    );
  }
  if (state.error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-md p-8 max-w-md text-center">
          <XCircle className="w-12 h-12 text-rose-500 mx-auto mb-3" />
          <h1 className="text-xl font-bold text-[#0a0a2e]">Codice QR non valido</h1>
          <p className="text-slate-600 text-sm mt-2">{state.error}</p>
          <p className="text-xs text-slate-400 mt-4">Contatta AMA Sicurezza per un&apos;etichetta aggiornata.</p>
        </div>
      </div>
    );
  }

  const d = state.data;
  const isClosed = d.case_status === "chiusa";

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-fuchsia-50/30 to-purple-50/30 p-4 sm:p-8" data-testid="public-track-page">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <AmaLogo size={36} />
          <div className="text-xs text-slate-500">Portale di tracciamento pratica</div>
        </div>

        <div className="bg-white rounded-2xl shadow-md p-6 sm:p-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-full bg-fuchsia-100 flex items-center justify-center">
              <Wrench className="w-5 h-5 text-[#d63384]" />
            </div>
            <div>
              <div className="text-xs uppercase font-semibold tracking-widest text-[#d63384]">Pratica di assistenza</div>
              <h1 className="text-2xl font-heading font-bold text-[#0a0a2e]" data-testid="track-case-number">{d.case_number}</h1>
            </div>
          </div>
          <div className="text-sm text-slate-600 mt-1">Cliente: <span className="font-semibold text-[#0a0a2e]" data-testid="track-customer">{d.customer_name || "-"}</span></div>
          <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500 mt-2">
            <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> Accettata il {formatDateIT(d.created_at)}</span>
            {d.updated_at && <span className="inline-flex items-center gap-1">· Ultimo aggiornamento {formatDateIT(d.updated_at)}</span>}
            {isClosed && d.delivery_date && (
              <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold">
                <CheckCircle2 className="w-3.5 h-3.5" /> Consegnata il {formatDateIT(d.delivery_date)}
              </span>
            )}
          </div>

          {d.approval_url && (
            <div className="mt-5 bg-gradient-to-r from-fuchsia-50 to-purple-50 border border-fuchsia-100 rounded-xl p-4 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <ShieldCheck className="w-6 h-6 text-[#6f42c1]" />
                <div>
                  <div className="font-semibold text-[#0a0a2e]">È disponibile un preventivo</div>
                  <div className="text-xs text-slate-600">Puoi accettare o rifiutare l&apos;offerta.</div>
                </div>
              </div>
              <a
                href={d.approval_url}
                data-testid="track-approval-link"
                className="inline-flex items-center gap-2 rounded-full bg-[#d63384] hover:bg-[#c02773] text-white px-4 py-2 text-sm font-semibold shadow-sm whitespace-nowrap"
              >
                <Paperclip className="w-4 h-4" /> Vedi preventivo
              </a>
            </div>
          )}

          <div className="mt-6 space-y-3">
            <div className="text-xs uppercase tracking-widest text-[#6f42c1] font-semibold">Prodotti in assistenza</div>
            {(d.items || []).map((it) => (
              <div key={it.item_number} data-testid={`track-item-${it.item_number}`} className="border border-slate-200 rounded-xl p-3 sm:p-4 bg-slate-50/60">
                <div className="flex items-start gap-3">
                  <Package className="w-5 h-5 text-[#6f42c1] mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-sm font-bold text-[#0a0a2e]">{it.item_number}</div>
                    <div className="text-sm text-slate-700 truncate">
                      {[it.manufacturer_name, it.product_code, it.model].filter(Boolean).join(" · ")}
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="inline-flex items-center gap-1 rounded-full bg-white border border-slate-200 px-2.5 py-0.5 text-xs font-semibold text-[#0a0a2e]">
                        <MapPin className="w-3 h-3" /> {STATUS_LABELS[it.current_status] || (it.current_status || "-").replace(/_/g, " ")}
                      </span>
                      {it.updated_at && <span className="text-[11px] text-slate-500">agg. {formatDateIT(it.updated_at)}</span>}
                    </div>
                    {it.last_customer_visible_note && (
                      <div className="text-xs text-slate-600 italic mt-1">&ldquo;{it.last_customer_visible_note}&rdquo;</div>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {(!d.items || d.items.length === 0) && (
              <div className="text-sm text-slate-500 italic text-center py-4">Nessun prodotto associato alla pratica.</div>
            )}
          </div>
        </div>

        <div className="mt-6 text-center">
          <a href="/login" className="text-xs text-slate-500 hover:text-[#d63384]">
            Sei un operatore AMA? <span className="font-semibold underline">Accedi al portale</span>
          </a>
        </div>
        <div className="mt-8 text-[11px] text-slate-400 text-center">
          © AMA Sicurezza S.r.l. — Le informazioni mostrate riguardano esclusivamente la pratica identificata da questo QR e non contengono dati sensibili.
        </div>
      </div>
    </div>
  );
}
